import { handle401Error } from './apiHelpers';
import { logger } from './logger';

interface ParsedResponse<T = any> {
  status: number;
  headers: Headers;
  statusText: string;
  data?: T;
}

function parseJSON(res: Response): Promise<ParsedResponse> | ParsedResponse {
  const response: ParsedResponse = {
    status: res.status,
    headers: res.headers,
    statusText: res.statusText,
  };

  if (res.status !== 500 && res.status !== 503) {
    return res.text().then(text => {
      response.data = text ? JSON.parse(text) : {};

      return response;
    });
  }

  return response;
}

interface ParsedFileResponse extends Response {
  data?: {
    fileBlobUrl: string;
    fileName: string | null;
  };
}

const REFRESH_PATH = 'refresh';

function parseFile(res: Response): Promise<ParsedFileResponse> {
  // todo: should always work with Content-Disposition header,
  // but because of Cors on gitlab ENV doesn't work for some reason
  // so name can be null, please note when use it in your saga actions for files downloads
  let fileName: string | null = null;

  const contentDisposition = res.headers.get('Content-Disposition');

  if (contentDisposition && contentDisposition.split('filename="')[1]) {
    [fileName] = contentDisposition.split('filename="')[1].split('"');
  }

  return res.blob().then(blob => {
    const fileUrl = URL.createObjectURL(blob);
    const parsedRes = res as ParsedFileResponse;
    parsedRes.data = { fileBlobUrl: fileUrl, fileName };

    return parsedRes;
  });
}

function parse(
  res: Response,
): Promise<ParsedResponse | ParsedFileResponse | Response> | ParsedResponse | ParsedFileResponse | Response {
  const contentType: string | null = res.headers.get('Content-Type')
    ? res.headers.get('Content-Type')!.split(';')[0]
    : null;

  switch (contentType) {
    case 'application/json':
      return parseJSON(res);
    case 'application/pdf':
    case 'text/csv':
    case 'image/jpeg':
    case 'image/png':
    case 'image/jpg':
    case 'image/gif':
    case 'application/octet-stream':
      return parseFile(res);
    default:
      return res;
  }
}

interface ErrorResponseData {
  errors?: {
    messages: string[];
    data?: {
      validations?: any;
    };
  };
  status?: number;
  error?: string;
  [key: string]: any;
}

interface CheckDataOptions {
  method: string;
  [key: string]: any;
}

interface SuccessResponse {
  success?: Record<string, any>;
  [key: string]: any;
}

export interface IApiError extends Error {
  /** The HTTP status, where one was read. Absent on a 401, a 403, a 500, a 503 and a 404 naming no errors. */
  code?: number;
  /** The server's own sentence, without the status line the message is built around. */
  detail?: string;
  /** The bare validations map, and only when the body carried one. */
  response?: any;
  /** `SyntaxError` where a body typed as JSON was not JSON. See `isTransportFailure` for the other half. */
  name: string;
  /** Set only where `fetch` itself rejected, so nothing answered at all. */
  isTransportFailure?: boolean;
}

function checkData(
  response: ParsedResponse<ErrorResponseData | SuccessResponse> & { status: number; statusText: string; data?: any },
  options: CheckDataOptions,
): any {
  if (response.status === 401) {
    throw new Error(401 as any);
  } else if (response.status === 403) {
    const { errors } = response.data || {};

    if (errors) {
      const message = errors.messages.join(' ');
      throw new Error(`403:${message}`);
    } else {
      throw new Error(403 as any);
    }
  } else if (response.status >= 200 && response.status < 300) {
    if (options.method.toLowerCase() !== 'get') {
      // Only a JSON or file body is parsed, so a 204 — or any 2xx typed as something else — arrives with no
      // `data` at all. Reading `success` off it raises a TypeError a caller cannot tell from a lost connection.
      if (!response.data) {
        const error: IApiError = new Error(`${response.status} answered without a readable body`);
        error.code = response.status;

        throw error;
      }

      return {
        ...response.data.success,
        status: response.status,
      };
    }

    return response;
  } else if (
    response.status !== 500 &&
    response.status !== 503 &&
    (response.status !== 404 || !!response?.data?.errors)
  ) {
    // Everything reaching here carries its status to the caller on `code`. A body may still be absent — a
    // proxy's HTML page on a 502 — so nothing is read off `data` without a guard.
    const { data } = response || {};
    const { messages } = data?.errors || {};

    let message = '';
    // The server's own sentence, apart from the status line it is built into: the only half worth showing.
    let detail = '';

    if (data?.errors) {
      const firstMessage = Array.isArray(messages) && messages.length > 0 ? messages[0] : '';
      detail = typeof firstMessage === 'string' ? firstMessage : '';
      message = `${response.status} ${response.statusText} ${firstMessage}`;
    } else {
      detail = typeof data?.error === 'string' ? data.error : '';
      message = `${data?.status ?? response.status} ${data?.error ?? response.statusText}`;
    }

    const error: IApiError = new Error(message);
    error.code = response.status;

    if (detail) {
      error.detail = detail;
    }

    if (response?.data?.errors?.data?.validations) {
      error.response = response?.data?.errors?.data?.validations;
    }

    throw error;
  } else {
    const message = `${response.status} ${response.statusText ? response.statusText : 'Error occurred'}`;
    const error = new Error(message);

    throw error;
  }
}

function processError(err: IApiError): never {
  const error: IApiError = new Error(err.message);
  // Carried over so a caller can still tell what went wrong: the parse failure by name, and a request
  // nothing answered by the flag `executeRequest` stamps on it.
  error.name = err.name;
  error.code = err.code;
  error.detail = err.detail;
  error.response = err.response;
  error.isTransportFailure = err.isTransportFailure;
  logger.error(error);
  throw error;
}

interface RequestOptions extends RequestInit {
  [key: string]: any;
}

interface RequestResult {
  [key: string]: any;
}

async function executeRequest(url: string, options: RequestOptions): Promise<RequestResult> {
  const checkDataOptions: CheckDataOptions = {
    ...options,
    'OS-Type': 'web',
    method: options.method ? options.method : 'get',
  };

  const result = await fetch(url, options)
    .catch((error: IApiError) => {
      // Stamped where it is known rather than inferred downstream: only `fetch` rejecting means nothing
      // answered, and everything after this point had a response to read.
      error.isTransportFailure = true;

      throw error;
    })
    .then(parse)
    .then(res => checkData(res, checkDataOptions));

  return { ...result };
}

export default async function request(url: string, options: RequestOptions): Promise<RequestResult> {
  const requestOptions: RequestOptions = {
    ...options,
    mode: 'cors',
    credentials: 'include',
  };

  try {
    return await executeRequest(url, requestOptions);
  } catch (error: any) {
    const statusCode = Number(error?.code ?? error?.message);

    if (statusCode !== 401) {
      processError(error);
    }

    const isRefreshRequest = typeof url === 'string' && url.includes(REFRESH_PATH);

    try {
      return await handle401Error({
        error,
        isRefreshRequest,
        retryFn: () => {
          return executeRequest(url, requestOptions);
        },
      });
    } catch (refreshError: any) {
      processError(refreshError);
    }
  }
}
